import { baseApi } from 'shared/api/baseApi'

import {
    CHILD_MATCHES_TAG,
    GROUP_MATCHES_TAG,
    GROUP_TAG,
    MATCHES_TAG,
    PHASES_TAG,
    RACES_TAG,
} from 'shared/api/tags'

import type { Competition, MatchesResponseParams } from 'entities/competitions'
import type { Match } from 'entities/phases'
import type { ChildMatch } from '../model'

interface Params {
    groupId: string
    matchType?: string
    include?: string
}

export const phaseApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getGroups: build.query<Match[], { phaseId: string }>({
            query: ({ phaseId }) => ({
                url: `phases/${phaseId}/groups`,
                method: 'GET',
            }),
            keepUnusedDataFor: 5,
            providesTags: [GROUP_TAG],

            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),

        getGroupMatches: build.query<Match[], Params>({
            query: ({ matchType, include, groupId }) => ({
                url: `groups/${groupId}/matches`,
                method: 'GET',
                params: {
                    matchType,
                    include,
                },
            }),
            keepUnusedDataFor: 5,
            providesTags: (_result, _error, { groupId }) => [
                { type: RACES_TAG, id: groupId },
                { type: GROUP_MATCHES_TAG, id: groupId },
            ],

            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return endpointName + queryArgs.groupId
            },

            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),

        createGroupMatches: build.mutation<Match[], { groupId: string }>({
            query: ({ groupId }) => ({
                url: `groups/${groupId}/matches`,
                method: 'POST',
            }),
            invalidatesTags: [
                RACES_TAG,
                PHASES_TAG,
                GROUP_MATCHES_TAG,
                GROUP_TAG,
            ],
        }),

        // Endpoint for the Berger system matches generation
        generateGroupMatches: build.mutation<Match[], { groupId: string }>({
            query: ({ groupId }) => ({
                url: `groups/${groupId}/generate-berger-group`,
                method: 'POST',
                body: { type: 'standard' },
            }),
            // Invalidate specific group tags when matches are generated
            invalidatesTags: (_result, _error, { groupId }) => [
                { type: RACES_TAG, id: groupId },
                { type: GROUP_MATCHES_TAG, id: groupId },
                { type: GROUP_TAG },
                PHASES_TAG,
            ],
        }),

        getMatches: build.query<
            DataWithMeta<Competition>,
            MatchesResponseParams
        >({
            query: (params) => ({
                url: `matches`,
                method: 'GET',
                params,
            }),
            providesTags: [MATCHES_TAG],
        }),

        getChildMatches: build.query<
            ChildMatch[],
            { matchId: string; include?: string }
        >({
            query: (params) => ({
                url: `matches/${params.matchId}/child-matches`,
                method: 'GET',
                params: {
                    include: params.include,
                },
            }),
            providesTags: [CHILD_MATCHES_TAG],
        }),

        getSingleMatch: build.query<
            Match,
            { matchId: string; include?: string }
        >({
            query: ({ matchId, include }) => ({
                url: `matches/${matchId}`,
                method: 'GET',
                params: { include },
            }),
            providesTags: [MATCHES_TAG],
        }),
    }),
})

export const {
    useGetGroupsQuery,
    useGetGroupMatchesQuery,
    useCreateGroupMatchesMutation,
    useGetChildMatchesQuery,
    useGetMatchesQuery,
    useLazyGetMatchesQuery,
    useGetSingleMatchQuery,
    useGenerateGroupMatchesMutation,
} = phaseApi
